import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialogClose, MatDialogRef} from "@angular/material/dialog";
import {MatError, MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatTooltip} from "@angular/material/tooltip";
import {finalize} from "rxjs/operators";
import {UtilService} from "../../services/util.service";
import {BusyService} from "../../services/busy.service";
import {DomainService} from "../../services/domain.service";
import {Constants} from "../../util/constants";
import {form, FormField, required, validate} from "@angular/forms/signals";
import {FieldErrorsComponent} from "../../controls/field-errors/field-errors.component";

interface FlagModel {
    name: string;
    preventsDelete: boolean;
}

@Component({
    imports: [
        FormsModule,
        MatButton,
        MatDialogClose,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatCheckbox,
        MatTooltip,
        FieldErrorsComponent,
        MatError,
        FormField
    ],
    templateUrl: './create-flag-dialog.component.html',
    styleUrl: './create-flag-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateFlagDialogComponent {

    private existingFlags: string[] = inject(MAT_DIALOG_DATA);

    flagModel = signal<FlagModel>({
        name: '',
        preventsDelete: true,
    });
    flagForm = form(this.flagModel, (schemaPath) => {
        required(schemaPath.name, {message: Constants.ERR_REQUIRED});
        validate(schemaPath.name, ({value}) => {
            if (this.existingFlags.includes(value().toLowerCase())) {
                return {kind: 'taken', message: "Flag name already taken"}
            }
            return null;
        })
    });

    constructor(private busyService: BusyService,
                private domainService: DomainService,
                private utilService: UtilService,
                private dialogRef: MatDialogRef<CreateFlagDialogComponent>) {
    }

    canSubmit = computed(() => {
        // We can submit if the form is valid and the name doesn't coincide with any existing name
        return this.flagForm().valid() ;
    });

    submit(event: SubmitEvent) {
        event.preventDefault();
        this.busyService.showBusy();
        const name: string = this.flagModel().name.toLowerCase();
        const preventsDelete: boolean = this.flagModel().preventsDelete;
        this.domainService.createFlag(name, preventsDelete).pipe(
            finalize(() => {this.busyService.showNotBusy()})
        ).subscribe({
            next: () => {
                this.dialogRef.close(true);
            },
            error: this.utilService.getErrorHandler("Error creating new flag")
        });
    }
}
